import type { ReactNode } from 'react';
import type { ClientCard } from '@/api/base-types';
import { Kv } from '@/components/kit/Layout';
import { formatPhone, years } from '@/lib/phone';
import { NickLink } from '../shared/NickLink';
import { rolesLine, ruDate } from '../shared/labels';

/**
 * Поля блока «Данные» клиента — один список на всю систему: карточка
 * клиента и блок клиента в сделке (Road Map п. 12) берут его отсюда,
 * поэтому новое поле клиента появляется в обоих местах сразу.
 *
 * `compact` — для чужой карточки: пустые поля не выводятся, зато
 * добавлены роли, команды и студия — в карточке клиента у них свои блоки.
 */
export function ClientDataRows({ client: c, compact }: { client: ClientCard; compact?: boolean }) {
  const needsPhone = c.roles.some((r) => r !== 'participant');
  const dim = (t: string) => <span className="dim">{t}</span>;
  const rows: { k: string; v: ReactNode; empty: boolean }[] = [
    { k: 'Фамилия, имя, отчество', v: c.fullName, empty: false },
    {
      k: 'Дата рождения',
      empty: !c.birthDate,
      v: c.birthDate ? (
        <>
          <span className="num">{ruDate(c.birthDate)}</span>{' '}
          {c.age !== null ? <span className="muted">· {years(c.age)}</span> : null}
        </>
      ) : c.roles.includes('participant') ? (
        <span className="wtx">не указана — нужна участнику</span>
      ) : (
        dim('не указана')
      ),
    },
    {
      k: 'Телефон',
      empty: !c.phone,
      v: c.phone ? (
        <span className="num">{formatPhone(c.phone)}</span>
      ) : needsPhone ? (
        <span className="wtx">не указан — нужен для роли</span>
      ) : (
        dim('не указан')
      ),
    },
    { k: 'Почта', empty: !c.email, v: c.email ? <span className="brk">{c.email}</span> : dim('не указана') },
    { k: 'Город', empty: !c.city, v: c.city?.name ?? dim('не указан') },
    {
      k: 'Telegram',
      empty: !c.telegramUsername,
      v: (
        <>
          <NickLink nick={c.telegramUsername} network="telegram" />{' '}
          <span className="muted">· бот {c.tgConnected ? 'подключён' : 'не подключён'}</span>
        </>
      ),
    },
    { k: 'Instagram', empty: !c.instagram, v: <NickLink nick={c.instagram} network="instagram" /> },
  ];

  if (compact) {
    const teams = c.teams.filter((t) => !t.membership?.leftOn);
    const studios = [
      ...new Set([
        ...c.studios.map((s) => s.studio.name),
        ...teams.flatMap((t) => (t.team.studio ? [t.team.studio.name] : [])),
      ]),
    ];
    rows.push(
      { k: 'Роли', empty: !c.roles.length, v: rolesLine(c.roles, c.studioRoles) },
      {
        k: 'Команды',
        empty: !teams.length,
        v: teams.map((t) => `${t.team.name}${t.isHead ? ' · руководитель' : ''}`).join(', '),
      },
      { k: 'Студия', empty: !studios.length, v: studios.map((s) => `«${s}»`).join(', ') },
    );
  }

  rows.push(
    {
      k: 'Ответственный',
      empty: !c.responsible,
      v: c.responsible ? (
        <>
          {c.responsible.fullName}
          {c.roles.includes('head') ? <span className="muted"> · и за его команды</span> : null}
        </>
      ) : c.roles.includes('head') ? (
        <span className="wtx">не назначен</span>
      ) : (
        dim('не назначен')
      ),
    },
    {
      k: 'В базе с',
      empty: false,
      v: <span className="num">{ruDate(c.createdAt)}</span>,
    },
  );

  return (
    <>
      {rows
        .filter((r) => !(compact && r.empty))
        .map((r) => (
          <Kv key={r.k} k={r.k}>
            {r.v}
          </Kv>
        ))}
    </>
  );
}
